import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { Collapsible } from '~/components/ui/Collapsible';
import { MessageGradient } from '~/components/ui/MessageGradient';
import { useOnScreen } from '~/lib/hooks/useOnScreen';
import { classNames } from '~/utils/classNames';

import styles from './PinnedMessage.module.scss';

interface PinnedMessageContext {
  onScreen: boolean;
}

export interface PinnedMessageProps {
  className?: string;
  title: ReactNode | ((context: PinnedMessageContext) => ReactNode);
  toggle?: ReactNode | ((context: PinnedMessageContext) => ReactNode);
  children?: ReactNode | ((context: PinnedMessageContext) => ReactNode);
  actions: ReactNode | ((context: PinnedMessageContext) => ReactNode);
  sticky: boolean;
  isLast: boolean;
  messageBoxHeight: number;
  toggled: boolean;
  messageClassName?: string;
  scrollToBottom: ScrollToBottom;
  setToggled(toggled: boolean): void;
  onStickyHeight: (height: number) => void;
}

export const PinnedMessage = memo((allProps: PinnedMessageProps) => {
  const {
    sticky: isSticky,
    title,
    toggle,
    children,
    actions,
    className,
    messageBoxHeight,
    isLast,
    toggled,
    messageClassName,
    setToggled,
    onStickyHeight,
    scrollToBottom,
    ...props
  } = allProps;

  const ref = useRef<HTMLDivElement>();

  const onScreen = useOnScreen(ref, `0px 0px -${messageBoxHeight}px 0px`);

  const [height, setHeight] = useState(0);

  const context = useMemo(() => {
    return {
      onScreen,
    };
  }, [onScreen]);

  const renderedTitle = typeof title === 'function' ? title(context) : title;
  const renderedToggle = typeof toggle === 'function' ? toggle(context) : toggle;
  const renderedChildren = typeof children === 'function' ? children(context) : children;
  const renderedActions = typeof actions === 'function' ? actions(context) : actions;

  const observer = useMemo(() => {
    return new ResizeObserver(([entry]) => {
      setHeight(entry.contentRect.height);
    });
  }, []);

  const refCallback = useCallback((element: HTMLDivElement | null) => {
    observer.disconnect();

    if (element) {
      observer.observe(element);
    } else {
      setHeight(0);
    }

    ref.current = element ?? undefined;
  }, []);

  const sticky = isLast && !toggled && isSticky;

  useLayoutEffect(() => {
    onStickyHeight(sticky ? height && height + 14 : 0);

    return () => {
      if (sticky) {
        onStickyHeight(0);
      }
    };
  }, [height, sticky]);

  return (
    <div
      ref={refCallback}
      className={classNames(
        'pinned-message',
        {
          sticky,
          [styles.StickyPinnedMessage]: sticky,
        },
        'px-[var(--chat-padding)]',
        className,
      )}
      style={{
        bottom: messageBoxHeight,
        marginBottom: sticky ? '-1.5rem' : '0px',
      }}
      {...props}
    >
      {sticky && <MessageGradient />}
      <div className="bg-bolt-elements-background-depth-1">
        <div
          className={classNames(
            "gap-3 flex flex-col p-4 w-full rounded-md bg-bolt-elements-messages-background relative before:content-[''] before:pointer-events-none before:absolute overflow-hidden before:inset-0",
            messageClassName,
          )}
        >
          {title && (
            <h4 className="flex items-center gap-2 text-bolt-elements-textPrimary font-medium text-sm">
              {renderedTitle}
            </h4>
          )}

          {toggle && (
            <Collapsible
              title={
                <div className="flex items-center gap-2.5 p-3 grow text-bolt-elements-textPrimary leading-5 text-sm">
                  {renderedToggle}
                </div>
              }
              titleClassName="bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2"
              toggleButtonClassName="bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2"
              open={toggled}
              scrollToBottom={scrollToBottom}
              onClick={() => setToggled(!toggled)}
              onToggle={setToggled}
            >
              <div className="flex flex-col gap-4 p-5 text-left bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 rounded-b-lg">
                {renderedChildren}
              </div>
            </Collapsible>
          )}

          {renderedActions}
        </div>
        {sticky && <div className="h-4" />}
      </div>
    </div>
  );
});

interface ToggleStateHookDependencies {
  isLast: boolean;
  scrollToBottom: ScrollToBottom;
}

export function usePinnedMessageToggleState({ scrollToBottom, isLast }: ToggleStateHookDependencies) {
  const [toggled, updateToggled] = useState(false);

  const setToggled = useCallback(
    async (toggled: boolean) => {
      if (toggled && isLast) {
        await scrollToBottom({ animation: { mass: 0.9 } });
      }

      updateToggled(toggled);
    },
    [isLast, scrollToBottom],
  );

  return {
    toggled,
    setToggled,
  };
}
